import { FC, useMemo, useState, useEffect, useCallback } from 'react';
import { SearchIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { RadixTable, TColumn, Input, Button } from '@features/ui';
import { UserNoteItem } from '@src/types/users';
import {
  getUserNotes,
  updateUserNote,
  createUserNote,
} from '@src/services/users';
import { formatDateTimeShort } from '@lib/utils';

import { AddNoteDialog } from './AddNoteDialog';

type Row = {
  id: number;
  dateTime: string;
  admin: string;
  message: string;
  userId: number;
};

interface UserNotesProps {
  userId: number;
}

export const UserNotes: FC<UserNotesProps> = ({ userId }) => {
  const [data, setData] = useState<Row[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingMessage, setEditingMessage] = useState<string>('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const fetchNotes = useCallback(
    async (keyword?: string, isInitial = false) => {
      if (!userId) return;

      if (isInitial) {
        setInitialLoading(true);
      } else {
        setSearchLoading(true);
      }

      try {
        const response = await getUserNotes(userId, keyword);
        const notes = response.data.notes;

        const mappedData: Row[] = notes.map((note: UserNoteItem) => ({
          id: note.id,
          dateTime: note.created_at,
          admin: `${note.admin?.fullname || ''}`,
          message: note.content || '',
          userId: note.user_id,
        }));

        // Sort by dateTime (newest first)
        mappedData.sort(
          (a, b) =>
            new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()
        );

        setData(mappedData);
      } catch (error) {
        console.error('Error fetching notes:', error);
        toast.error('Failed to load notes');
      } finally {
        if (isInitial) {
          setInitialLoading(false);
        } else {
          setSearchLoading(false);
        }
      }
    },
    [userId]
  );

  useEffect(() => {
    fetchNotes(undefined, true);
  }, [fetchNotes]);

  const handleEditMessage = (noteId: number, currentMessage: string) => {
    setEditingId(noteId);
    setEditingMessage(currentMessage);
  };

  const handleSaveMessage = useCallback(
    async (noteId: number) => {
      if (!userId) return;

      try {
        await updateUserNote(userId, noteId, editingMessage);
        toast.success('Note updated successfully');

        // Update local data
        setData((prev) =>
          prev.map((item) =>
            item.id === noteId ? { ...item, message: editingMessage } : item
          )
        );

        setEditingId(null);
        setEditingMessage('');
      } catch (error) {
        console.error('Error updating note:', error);
        toast.error('Failed to update note');
      }
    },
    [userId, editingMessage]
  );

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingMessage('');
  };

  const handleSearch = useCallback(() => {
    const keyword = searchInput.trim();

    fetchNotes(keyword || undefined, false);
  }, [searchInput, fetchNotes]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleAddNote = useCallback(
    async (content: string) => {
      if (!userId) return;

      try {
        await createUserNote(userId, content);
        toast.success('Note added successfully');
        // Refresh notes list
        await fetchNotes(searchInput.trim() || undefined, false);
      } catch (error) {
        console.error('Error adding note:', error);
        toast.error('Failed to add note');
        throw error;
      }
    },
    [userId, fetchNotes, searchInput]
  );

  const columns: TColumn<Row>[] = useMemo(
    () => [
      {
        title: 'DATE/TIME',
        render: (r) => (
          <span className="text-xs">
            {r?.dateTime ? formatDateTimeShort(r.dateTime) : ''}
          </span>
        ),
      },
      {
        title: 'ADMIN',
        render: (r) => <span className="text-xs">{r?.admin ?? ''}</span>,
      },
      {
        title: 'MESSAGE',
        render: (r) => (
          <div className="flex items-center gap-2 w-full">
            {editingId === r?.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  className="flex-1"
                  textSize="xs"
                  value={editingMessage}
                  onChange={(e) => setEditingMessage(e.target.value)}
                />
                <button
                  className="px-3 py-1 bg-blue text-white rounded text-xs hover:bg-blue/90"
                  onClick={() => r && handleSaveMessage(r.id)}
                >
                  Save
                </button>
                <button
                  className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full gap-2">
                <span className="text-xs flex-1">{r?.message ?? ''}</span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => r && handleEditMessage(r.id, r.message)}
                >
                  Edit
                </Button>
              </div>
            )}
          </div>
        ),
      },
    ],
    [editingId, editingMessage, handleSaveMessage]
  );

  if (initialLoading) {
    return <div className="text-center py-8">Loading notes...</div>;
  }

  return (
    <>
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2 max-w-md flex-1">
          <Input
            className="flex-1"
            disabled={searchLoading}
            placeholder="Search notes..."
            startIcon={<SearchIcon size={16} />}
            textSize="sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button
            disabled={searchLoading}
            loading={searchLoading}
            size="sm"
            variant="secondary"
            onClick={handleSearch}
          >
            Go
          </Button>
        </div>
        <Button size="sm" variant="lime" onClick={() => setAddDialogOpen(true)}>
          New
        </Button>
      </div>
      <RadixTable columns={columns} data={data} />
      <AddNoteDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSubmit={handleAddNote}
      />
    </>
  );
};
